/**
 * TemplateMedicineComposer — the "how do medicines get INTO a template" step,
 * kept as one small swappable piece.
 *
 * Today it is the typed-shorthand parser the Rx Templates card has always used
 * (D13/AC4: the organisation-list picker on that card is PARKED). It is a
 * separate component precisely so that picker can replace it later by swapping
 * this one import — `TemplateSaveForm` only knows `onAdd(rows)`.
 *
 * It NEVER writes a template: it hands parsed rows up, and the form's own
 * tick-list decides what is saved.
 */
import React, { useCallback, useRef, useState } from 'react';
import { parsePrescription } from '../../services/api/visitMetadataApi';

/** Infer item_type from the parser's `form` field (mirrors the card's rule). */
export function inferItemType(form) {
  if (!form) return 'medicine';
  const f = String(form).toLowerCase();
  if (f === 'vaccine') return 'vaccine';
  if (f === 'injection') return 'injection';
  return 'medicine';
}

const HINTS = [
  { key: 'tab', label: '💊 Medicine', example: 'tab pcm 650 1-0-1 5d af' },
  { key: 'vacc', label: '🛡️ Vaccine', example: 'vacc covishield' },
  { key: 'inj', label: '💉 Injection', example: 'inj ceftriaxone 1g' },
];

export default function TemplateMedicineComposer({ onAdd, isDark = false }) {
  const [rawInput, setRawInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleParse = useCallback(async () => {
    const text = rawInput.trim();
    if (!text || parsing) return;          // single-flight: one parse per click
    setParsing(true);
    setError(null);
    try {
      const data = await parsePrescription(text);
      const results = (data?.results || []).filter((r) => r.medicine && r.medicine !== 'Unknown');
      if (results.length === 0) {
        setError('Could not parse. Try: "tab pcm 650 1-0-1 5d"');
        return;
      }
      onAdd(results.map((r, i) => ({
        _id: `new_${Date.now()}_${i}`,
        medicine: r.medicine || r.medicine_input || 'Unknown',
        medicine_input: r.medicine_input || text,
        strength: r.strength || null,
        form: r.form || null,
        item_type: inferItemType(r.form),
        dosage_schedule: r.dosage_schedule || null,
        frequency: r.frequency || null,
        timing: r.timing || null,
        duration: r.duration || null,
        notes: r.notes || null,
        is_resolved: r.is_resolved ?? true,
      })));
      setRawInput('');
    } catch (err) {
      setError(err?.message || 'Failed to parse prescription');
    } finally {
      setParsing(false);
    }
  }, [rawInput, parsing, onAdd]);

  const border = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.18)';
  const ink = isDark ? '#e2e8f0' : '#1e293b';
  const inkFaint = isDark ? '#94a3b8' : '#64748b';
  const controlBg = isDark ? '#1e293b' : '#ffffff';

  return (
    <div style={{ marginBottom: '0.4rem' }}>
      <div style={{ display: 'flex', gap: '0.375rem' }}>
        <input
          ref={inputRef}
          type="text"
          value={rawInput}
          aria-label="Add a medicine"
          onChange={(e) => { setRawInput(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleParse(); } }}
          placeholder='e.g. "tab pcm 650 1-0-1 5d af"'
          disabled={parsing}
          style={{
            flex: 1, boxSizing: 'border-box', padding: '0.4rem 0.6rem',
            borderRadius: '0.375rem', border: `1px solid ${border}`,
            background: controlBg, color: ink, fontSize: '0.75rem', outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={handleParse}
          disabled={parsing || !rawInput.trim()}
          style={{
            padding: '0.4rem 0.7rem', borderRadius: '0.375rem', border: 'none',
            background: '#0d9488', color: '#fff', fontSize: '0.75rem', fontWeight: 700,
            cursor: parsing || !rawInput.trim() ? 'default' : 'pointer',
            opacity: parsing || !rawInput.trim() ? 0.6 : 1, whiteSpace: 'nowrap',
          }}
        >{parsing ? '…' : '+ Add'}</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.3rem' }}>
        {HINTS.map((h) => (
          <button
            key={h.key}
            type="button"
            onClick={() => { setRawInput(h.example); inputRef.current?.focus(); }}
            title={`Try: ${h.example}`}
            style={{
              padding: '0.15rem 0.45rem', borderRadius: '0.375rem', cursor: 'pointer',
              border: `1px solid ${border}`, background: controlBg,
              color: inkFaint, fontSize: '0.625rem',
            }}
          >{h.label}</button>
        ))}
      </div>
      {error && (
        <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: isDark ? '#fca5a5' : '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
